import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  MagnifyingGlass, CaretUp, CaretDown, ArrowClockwise,
  CheckCircle, Question, FileXls,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtINR = (n) => {
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency", currency: "INR", maximumFractionDigits: 0,
    }).format(Number(n) || 0);
  } catch { return `₹${n}`; }
};
const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "2-digit",
    });
  } catch { return s; }
};
const fmtDurationDays = (d) => {
  if (!d || d <= 0) return "single day";
  if (d < 30) return `${d}d`;
  const m = Math.floor(d / 30);
  const r = d % 30;
  return r ? `${m}mo ${r}d` : `${m}mo`;
};

const SORT_COLS = [
  { id: "vendor",        label: "Party (cleaned)" },
  { id: "matched_party", label: "Tally Match" },
  { id: "last_date",     label: "Last Date" },
  { id: "last_amount",   label: "Last Payment" },
  { id: "total",         label: "Total" },
  { id: "duration_days", label: "Duration" },
  { id: "count",         label: "# Payments" },
];

export default function PaymentsHistoryTab({ refreshKey }) {
  const [loading, setLoading] = useState(true);
  const [data, setData]       = useState({ rows: [], total: 0, count: 0 });
  const [query, setQuery]     = useState("");
  const [sortBy, setSortBy]   = useState("total");
  const [sortDir, setSortDir] = useState("desc"); // "asc" | "desc"
  const [expanded, setExpanded] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/payments-history");
      setData(r.data || { rows: [], total: 0, count: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load payments history");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const onSort = (col) => {
    if (sortBy === col) {
      setSortDir((d) => d === "asc" ? "desc" : "asc");
    } else {
      setSortBy(col);
      setSortDir(col === "vendor" ? "asc" : "desc");
    }
  };
  const toggleExpand = (key) => {
    setExpanded((s) => {
      const ns = new Set(s);
      ns.has(key) ? ns.delete(key) : ns.add(key);
      return ns;
    });
  };

  const filteredSorted = useMemo(() => {
    const q = query.trim().toLowerCase();
    let rows = data.rows || [];
    if (q) {
      rows = rows.filter((r) =>
        (r.vendor || "").toLowerCase().includes(q)
        || (r.payments || []).some((p) =>
          (p.description || "").toLowerCase().includes(q)
          || (p.ref || "").toLowerCase().includes(q))
      );
    }
    const dir = sortDir === "asc" ? 1 : -1;
    rows = [...rows].sort((a, b) => {
      const va = a[sortBy] ?? "";
      const vb = b[sortBy] ?? "";
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
    return rows;
  }, [data.rows, query, sortBy, sortDir]);

  const filteredStats = useMemo(() => ({
    parties: filteredSorted.length,
    total:   filteredSorted.reduce((s, r) => s + (r.total || 0), 0),
    txns:    filteredSorted.reduce((s, r) => s + (r.count || 0), 0),
  }), [filteredSorted]);

  const downloadXlsx = async () => {
    // Build a simple CSV client-side (no extra backend trip needed).
    const headers = ["Party", "Matched in Tally?", "First Payment", "Last Payment", "Duration (days)", "# Payments", "Total Paid", "Last Amount", "Bank Accounts"];
    const lines = [headers.join(",")];
    filteredSorted.forEach((r) => {
      const cells = [
        `"${(r.vendor || "").replace(/"/g, '""')}"`,
        r.matched ? "Yes" : "No",
        r.first_date || "",
        r.last_date || "",
        r.duration_days || 0,
        r.count || 0,
        r.total || 0,
        r.last_amount || 0,
        `"${(r.accounts || []).join(" | ")}"`,
      ];
      lines.push(cells.join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `Payments History - ${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="payments-history-tab">
      {/* Header strip */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-heading font-bold text-lg text-slate-100">Payments History</h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Aggregated view of every bank debit grouped by party. Sourced from all uploaded bank statements (HDFC, ICICI, …).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            className="h-9 border-slate-700 text-slate-300 bg-slate-900 hover:bg-slate-800 hover:text-cyan-100"
            data-testid="payments-history-refresh"
          >
            <ArrowClockwise size={13} weight="bold" className="mr-1" />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={downloadXlsx}
            className="h-9 border-emerald-700 text-emerald-200 bg-slate-900 hover:bg-emerald-900/30 hover:text-emerald-100"
            data-testid="payments-history-download"
            disabled={filteredSorted.length === 0}
          >
            <FileXls size={13} weight="bold" className="mr-1" />
            CSV
          </Button>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <SummaryCard label="Parties" value={filteredStats.parties.toLocaleString("en-IN")} accent="cyan" />
        <SummaryCard label="Total paid" value={fmtINR(filteredStats.total)} accent="amber" mono />
        <SummaryCard label="Total payments" value={filteredStats.txns.toLocaleString("en-IN")} accent="slate" />
        <SummaryCard label="Matched in Tally" value={`${filteredSorted.filter((r) => r.matched).length} of ${filteredSorted.length}`} accent="emerald" />
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <MagnifyingGlass size={14} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter by party, description, or reference…"
          className="h-9 pl-8 bg-slate-950 border-slate-700 text-sm"
          data-testid="payments-history-search"
        />
      </div>

      {/* Main table */}
      <div className="rounded-md border border-slate-700 overflow-hidden">
        <div className="overflow-x-auto max-h-[640px] overflow-y-auto">
          <table className="w-full text-xs" data-testid="payments-history-table">
            <thead className="bg-slate-900 sticky top-0 z-10">
              <tr className="text-[10px] uppercase tracking-wider font-bold text-cyan-200">
                <th className="w-8 px-2 py-2"></th>
                {SORT_COLS.map((c) => (
                  <th
                    key={c.id}
                    onClick={() => onSort(c.id)}
                    className="text-left px-3 py-2 cursor-pointer hover:bg-slate-800 select-none"
                    data-testid={`payments-history-sort-${c.id}`}
                  >
                    <span className="inline-flex items-center gap-1">
                      {c.label}
                      {sortBy === c.id && (
                        sortDir === "asc"
                          ? <CaretUp size={10} weight="bold" />
                          : <CaretDown size={10} weight="bold" />
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={SORT_COLS.length + 1} className="text-center py-12 text-slate-500">Loading…</td></tr>
              )}
              {!loading && filteredSorted.length === 0 && (
                <tr><td colSpan={SORT_COLS.length + 1} className="text-center py-12 text-slate-500">
                  {query ? "No parties match your search." : "No bank debits found. Upload bank statements first."}
                </td></tr>
              )}
              {!loading && filteredSorted.map((r) => {
                const key = `${r.vendor}|${r.count}`;
                const open = expanded.has(key);
                return (
                  <React.Fragment key={key}>
                    <tr
                      onClick={() => toggleExpand(key)}
                      className={`border-t border-slate-800 cursor-pointer transition-colors ${open ? "bg-cyan-950/30" : "hover:bg-slate-900/60"}`}
                      data-testid={`payments-history-row-${key.replace(/[^a-z0-9]/gi, "_").toLowerCase()}`}
                    >
                      <td className="px-2 py-2 text-center text-slate-500">
                        {open ? <CaretDown size={12} weight="bold" /> : <CaretUp size={12} weight="bold" className="rotate-90" />}
                      </td>
                      <td className="px-3 py-2 max-w-[280px]">
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold text-slate-100 truncate" title={r.vendor}>{r.vendor || "—"}</span>
                          {r.matched
                            ? <CheckCircle size={11} weight="duotone" className="text-emerald-400 shrink-0" title="Matched to Tally party" />
                            : <Question size={11} weight="duotone" className="text-slate-600 shrink-0" title="No Tally match — raw bank narration" />
                          }
                        </div>
                        {r.accounts?.length > 0 && (
                          <div className="text-[9px] text-slate-500 uppercase tracking-wider mt-0.5">
                            via {r.accounts.join(", ")}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-400 font-mono">{fmtDate(r.last_date)}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-amber-200 font-semibold">{fmtINR(r.last_amount)}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-cyan-100 font-bold">{fmtINR(r.total)}</td>
                      <td className="px-3 py-2 text-slate-400 font-mono">{fmtDurationDays(r.duration_days)}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-300">{r.count}</td>
                    </tr>
                    {open && (
                      <tr className="bg-slate-950" data-testid={`payments-history-expand-${key.replace(/[^a-z0-9]/gi, "_").toLowerCase()}`}>
                        <td className="px-3 pb-3 pt-1" colSpan={SORT_COLS.length + 1}>
                          <div className="rounded-sm border border-cyan-900/40 bg-slate-900/60 overflow-hidden">
                            <div className="px-3 py-1.5 bg-slate-900 border-b border-cyan-900/40 flex items-center justify-between">
                              <div className="text-[10px] uppercase tracking-wider text-cyan-200 font-bold">
                                Every payment to <span className="text-cyan-100">{r.vendor}</span>
                              </div>
                              <div className="text-[10px] text-slate-500">
                                Window: {fmtDate(r.first_date)} → {fmtDate(r.last_date)} ({fmtDurationDays(r.duration_days)})
                              </div>
                            </div>
                            <table className="w-full text-[11px]">
                              <thead>
                                <tr className="bg-slate-900/60 text-[9px] uppercase tracking-wider text-slate-500">
                                  <th className="text-left px-3 py-1.5">Date</th>
                                  <th className="text-right px-3 py-1.5">Amount</th>
                                  <th className="text-left px-3 py-1.5">Bank</th>
                                  <th className="text-left px-3 py-1.5">Reference / Description</th>
                                </tr>
                              </thead>
                              <tbody>
                                {r.payments.map((p, i) => (
                                  <tr key={i} className="border-t border-slate-800/60">
                                    <td className="px-3 py-1 font-mono text-slate-300">{fmtDate(p.date)}</td>
                                    <td className="px-3 py-1 text-right font-mono tabular-nums text-amber-200">{fmtINR(p.amount)}</td>
                                    <td className="px-3 py-1 text-slate-400">{p.account || "—"}</td>
                                    <td className="px-3 py-1 text-slate-400 font-mono truncate max-w-[420px]" title={p.description}>
                                      {p.ref ? <span className="text-slate-500 mr-1">{p.ref}</span> : null}
                                      {p.description || "—"}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
            {!loading && filteredSorted.length > 0 && (
              <tfoot className="bg-slate-900 sticky bottom-0">
                <tr className="border-t-2 border-cyan-800 font-bold text-cyan-100">
                  <td className="px-2 py-2"></td>
                  <td className="px-3 py-2 uppercase tracking-wider text-[11px]">Grand Total</td>
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtINR(filteredStats.total)}</td>
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">{filteredStats.txns}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, accent = "cyan", mono = false }) {
  const accentMap = {
    cyan:    "border-cyan-800/40 text-cyan-200",
    amber:   "border-amber-800/40 text-amber-200",
    emerald: "border-emerald-800/40 text-emerald-200",
    slate:   "border-slate-700 text-slate-200",
  };
  return (
    <div className={`rounded-md border bg-slate-900/60 px-3 py-2 ${accentMap[accent]}`}>
      <div className="text-[9px] uppercase tracking-[0.2em] opacity-70 font-bold">{label}</div>
      <div className={`text-base font-bold ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}
